import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, List, ListOrdered, Link2, Eraser } from "lucide-react";

export function RichTextEditor({ value, onChange, placeholder = "Write your announcement…" }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      const safe = DOMPurify.sanitize(value || "", { ADD_ATTR: ["target", "rel"] });
      if (ref.current.innerHTML !== safe) ref.current.innerHTML = safe;
    }
  }, [value]);

  const emit = () => onChange(ref.current?.innerHTML || "");

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (url) exec("createLink", url.trim());
  };

  const btn = "h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-navy/10 text-navy transition-colors";

  return (
    <div className="rounded-xl border border-border overflow-hidden bg-white">
      <div className="flex items-center gap-1 border-b border-border bg-secondary px-2 py-1">
        <button type="button" title="Bold" className={btn} data-testid="rte-bold" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")}><Bold className="h-4 w-4" /></button>
        <button type="button" title="Italic" className={btn} data-testid="rte-italic" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")}><Italic className="h-4 w-4" /></button>
        <button type="button" title="Underline" className={btn} data-testid="rte-underline" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("underline")}><Underline className="h-4 w-4" /></button>
        <span className="w-px h-5 bg-border mx-1" />
        <button type="button" title="Bullet list" className={btn} data-testid="rte-ul" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")}><List className="h-4 w-4" /></button>
        <button type="button" title="Numbered list" className={btn} data-testid="rte-ol" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertOrderedList")}><ListOrdered className="h-4 w-4" /></button>
        <span className="w-px h-5 bg-border mx-1" />
        <button type="button" title="Insert link" className={btn} data-testid="rte-link" onMouseDown={(e) => e.preventDefault()} onClick={addLink}><Link2 className="h-4 w-4" /></button>
        <button type="button" title="Clear formatting" className={btn} data-testid="rte-clear" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("removeFormat")}><Eraser className="h-4 w-4" /></button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-testid="banner-editor"
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder}
        className="min-h-[120px] p-3 text-sm leading-relaxed text-navy focus:outline-none [&_a]:text-orange [&_a]:underline [&_a]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground"
      />
    </div>
  );
}
